import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useHistory } from 'react-router';
import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import _ from 'lodash';

import FixedLoader from '../../../components/FixedLoader/FixedLoader';
import TalentNavbar from '../../../components/TalentNavbar';
import TalentPageTitle from '../../../components/TalentNavbar/TalentPageTitle';
import TalentBackButton from '../../../components/TalentBackButton/TalentBackButton';
import Api from '../../../api';
import useActionApi from '../../../hooks/useActionApi';
import { usePersistedState } from '../../../hooks';
import entities from '../../../reduxStore/entityKeys';
import { HygienistProps, JobProps, HygienistScoreProps } from 'utils/types';
import { getPaymentTerms, getPaymentMethod } from '../../../utils';
import { getTalentParkingLabel } from 'utils/parking';
import { formatDowntimeLabel } from 'utils/downtime';
import { openExternalUrlFromLink } from 'utils/nativeApp';
import { convertDentalOfficeStatus } from '../ShiftDetailPage/components';
import { CompleteShiftModal } from '../ShiftDetailPage/components';
import { StyledButton } from '../../../components/Buttons/DefaultButton/DefaultButton.styles';
import { ShiftNotesPanel } from './components/ShiftNotesPanel';
import { AdminTasksDisplay } from '../AvailableJobPage/components/AdminTasksDisplay';

// Icons
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import TimeToLeaveOutlinedIcon from '@mui/icons-material/TimeToLeaveOutlined';
import DiningOutlinedIcon from '@mui/icons-material/DiningOutlined';
import DesktopMacOutlinedIcon from '@mui/icons-material/DesktopMacOutlined';
import TimelapseOutlinedIcon from '@mui/icons-material/TimelapseOutlined';
import WavesOutlinedIcon from '@mui/icons-material/WavesOutlined';
import BlurLinearOutlinedIcon from '@mui/icons-material/BlurLinearOutlined';
import LineAxisOutlinedIcon from '@mui/icons-material/LineAxisOutlined';
import SpaOutlinedIcon from '@mui/icons-material/SpaOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import SentimentSatisfiedAltOutlinedIcon from '@mui/icons-material/SentimentSatisfiedAltOutlined';
import LaptopMacOutlinedIcon from '@mui/icons-material/LaptopMacOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import heartFilled from '../../../images/heartFilled.svg';
import heartUnfilled from '../../../images/heartUnfilled.svg';

import styles from './ConfirmedOfferPage.module.scss';

dayjs.extend(isToday);

const WORKPLACE_SAFETY_GUARANTEE_URL = 'https://support.tempstars.com/hc/en-us/articles/360051064813-PPE-and-Infection-Control-for-Temping-Shifts';
const CANCELLATION_POLICY_URL = 'https://support.tempstars.com/hc/en-us/articles/360046619234-Cancelling-Shifts-Hygienists-Assistants';
const DIRECT_PAYMENT_URL = 'https://www.tempstars.com/direct_payment';
const SHORT_NOTICE_CANCELLATION_URL = 'https://www.tempstars.com/short-notice';

// ============ MAIN COMPONENT ============

const ConfirmedOfferPage: React.FC = () => {
    const history = useHistory();
    
    // ===== PAGE STATE =====
    const [trackData, setTrackData] = useState<any>(null);
    const [isFavouriteDentist, setFavouriteDentist] = useState<boolean>(false);
    const [updatingFavourite, setUpdatingFavourite] = useState<boolean>(false);
    const [cancellationStatsProps, setCancellationStatsProps] = useState<HygienistScoreProps | null>(null);
    const [hireJobsCount, setHireJobsCount] = useState<number>(0);

    const locationState = useMemo(
        () => history.location.state as any,
        [history.location.state]
    );

    const { jobId, job: jobData } = locationState || {};
    const [job, setJob] = useState<JobProps>(jobData || ({} as any));

    const [hygienist] = usePersistedState<HygienistProps>({
        entityName: entities.hygienist
    });

    // Refs to prevent infinite loops
    const hasFetchedFavourite = useRef<number | null>(null);
    
    // ===== OFFER LOGIC =====
    const offer = useMemo(() => {
        if (!_.isEmpty(job) && hygienist?.id) {
            return _.findLast(job?.partialOffers, (offer: any) => offer.hygienistId === hygienist.id);
        }
        return undefined;
    }, [job, hygienist?.id]);

    // ===== API CALLS =====
    const handleJobDetailsSuccess = useCallback((jobObj: any) => {
        if (_.isEmpty(jobObj)) return;
        setJob(jobObj);
    }, []);

    const handleFavouriteSuccess = useCallback((data: any) => {
        setFavouriteDentist(data?.isFavouriteDentist ?? false);
    }, []);

    const [getJobDetailsLoading, , getJobDetails] = useActionApi({
        apiMethod: Api.getJobDetails,
        onSuccess: handleJobDetailsSuccess
    });

    const [checkFavouriteLocationLoading, , checkFavouriteLocation] = useActionApi({
        apiMethod: Api.checkFavouriteLocation,
        onSuccess: handleFavouriteSuccess
    });

    const [statsLoading, , getStats] = useActionApi({
        apiMethod: Api.calculateCancellationStats,
        onSuccess: (data: HygienistScoreProps) => {
            setCancellationStatsProps(data);
        },
    });

    // ===== COMPUTED VALUES =====
    const shouldFetchJob = useMemo(() => _.isEmpty(jobData) && jobId, [jobData, jobId]);
    const isJobEmpty = useMemo(() => _.isEmpty(job), [job]);
    const hasValidShifts = useMemo(() => job?.shifts && job.shifts.length > 0, [job?.shifts]);

    const formattedPostedDate = useMemo(() => {
        return job.postedOn ? dayjs(job.postedOn).format('dddd, MMM D') : '';
    }, [job.postedOn]);

    const formattedShiftDate = useMemo(() => {
        return job.shifts?.[0]?.shiftDate ? dayjs(job.shifts[0].shiftDate).format('dddd, MMM D') : '';
    }, [job.shifts]);

    const shiftTime = useMemo(() => {
        if (!job.shifts?.[0]) return '';
        return `${job.shifts[0].postedStart} - ${job.shifts[0].postedEnd}`;
    }, [job.shifts]);

    const avgRecall = useMemo(() => job.location?.avgApptTime || '30 min', [job.location?.avgApptTime]);

    const baseHourlyRate = useMemo(() => {
        return offer?.hourlyRate || job?.hourlyRate;
    }, [job, offer]);

    const hourlyRate = useMemo(() => {
        return `$${baseHourlyRate}/hr`;
    }, [baseHourlyRate]);

    const paymentDescription = useMemo(() => {
        const terms = getPaymentTerms(job.paymentTerms);
        if (job.paymentMethod) {
            return `Office Pays: via ${getPaymentMethod(job.paymentMethod, job.location?.country)} ${terms}`;
        }
        return `Office Pays: ${terms} (No payment method specified by the office)`;
    }, [job.paymentTerms, job.paymentMethod, job.location?.country]);

    const safetyGuidelinesText = useMemo(() => {
        return `This office has confirmed they meet or exceed the ${job.location?.province || ''} guidelines for workplace safety and infection control.`;
    }, [job.location?.province]);

    const hasEndorsements = useMemo(() => {
        return !!job.location?.ppeEndorsed || !!job.location?.friendlyEndorsed || !!job.location?.equipEndorsed;
    }, [job.location]);

    /** T2-5795: downtime from Shift.gapUnpaid (0 = paid, 1 = unpaid) */
    const downtimeLabel = useMemo(() => {
        if (!job.shifts?.[0]) return null;
        return formatDowntimeLabel(job.shifts[0].gapUnpaid);
    }, [job.shifts]);

    const confirmationNote = useMemo(() => {
        const fn = hygienist?.firstName?.trim() || 'there';
        const prac = job.location?.practiceName?.trim() || 'this office';
        return `${fn}, you are confirmed for your shift — ${prac} and their patients are looking forward to seeing you.`;
    }, [hygienist?.firstName, job.location?.practiceName]);

    // Track data for analytics
    const memoizedTrackData = useMemo(() => {
        if (_.isEmpty(job) || !hasValidShifts) return null;
        return {
            isInstabookEnabled: !!job.Instabook,
            recommendedHourlyRate: job.proposedRate,
            currencyCode: job.location?.country ? 'USD' : 'CAD',
            dentalOfficeLocationId: job.locationId,
            dentalOfficeId: job.dentistId,
            dentalOfficeStatus: convertDentalOfficeStatus(job.location?.status ?? 0),
            shiftStartDate: new Date(
                job.startDate?.replace(/-/g, '/') + ' ' + job.shifts[0].postedStart
            ).toISOString(),
            shiftEndDate: new Date(
                job.startDate?.replace(/-/g, '/') + ' ' + job.shifts[0].postedEnd
            ).toISOString(),
            shiftId: job.shifts?.[0]?.id,
            shiftPostDate: job.postedOn ? new Date(job.postedOn).toISOString() : ''
        };
    }, [job, hasValidShifts]);

    // ===== EFFECTS =====
    useEffect(() => {
        if (shouldFetchJob && jobId) {
            getJobDetails(jobId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldFetchJob, jobId]);

    useEffect(() => {
        const locationId = job?.locationId;
        if (locationId && hasFetchedFavourite.current !== locationId) {
            hasFetchedFavourite.current = locationId;
            checkFavouriteLocation(locationId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [job?.locationId]);

    useEffect(() => {
        setTrackData(memoizedTrackData);
    }, [memoizedTrackData]);

    useEffect(() => {
        if (jobId) {
            getStats({ jobId });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobId]);

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const res = await Api.getAvailableHireJobsCount();
                if (mounted) setHireJobsCount(Number(res?.result ?? 0));
            } catch {}
        })();
        return () => { mounted = false; };
    }, []);

    // ===== HANDLERS =====
    const handleBackNavigation = useCallback(() => {
        history.push({ pathname: 'hygienistHome' });
    }, [history]);

    const handleJobBoardClick = useCallback(() => {
        (window as any).TempStars?.App?.goReact?.('jobPostsList');
    }, []);

    const handleToggleFavourite = async () => {
        if (!job) return;
        setUpdatingFavourite(true);
        try {
            if (isFavouriteDentist) {
                await Api.removeFavouriteDentistByLocationId(job.locationId);
            } else {
                await Api.setFavouriteDentist(job.dentistId, job.locationId);
            }
            checkFavouriteLocation(job.locationId);
        } catch {}
        setUpdatingFavourite(false);
    };

    const handleViewMap = () => {
        const hygienistAddress = hygienist?.lat + ',+' + hygienist?.lon;
        const officeAddress = job.location?.lat + ',+' + job.location?.lon;
        const url = `https://www.google.com/maps/dir/${hygienistAddress}/${officeAddress}`;
        if ((window as any).Cordova) {
            (window as any).cordova.InAppBrowser.open(url, '_system', 'location=yes');
        } else {
            window.open(url, '_blank', 'location=yes');
        }
    };

    const handleChat = useCallback(() => {
        history.push({ pathname: 'hygienistMessenger', state: { job } });
    }, [history, job]);

    const handleDocumentView = useCallback(() => {
        const photoUrl = (job?.location as any)?.photoUrl;
        if (!photoUrl) return;
        try {
            const fileUrl = photoUrl;
            // @ts-ignore
            if (window.cordova) {
                // @ts-ignore
                cordova.InAppBrowser.open(fileUrl, '_system', 'location=yes');
            } else {
                window.open(fileUrl, '_blank');
            }
        } catch (error) {
            console.error('Error handling document:', error);
            window.location.href = photoUrl;
        }
    }, [job?.location]);

    const handleCancelJob = useCallback(() => {
        history.push('hygienistCancelJob', { job, hygienist, cancellationStatsProps });
    }, [job, hygienist, cancellationStatsProps, history]);

    const hasDocument = !!(job?.location as any)?.photoUrl;


    // ===== LOADING STATE =====
    const isLoading = getJobDetailsLoading || checkFavouriteLocationLoading || statsLoading;

    if (isJobEmpty && !getJobDetailsLoading) {
        return (
            <div className={styles.pageContainer}>
                <FixedLoader loading={getJobDetailsLoading} />
            </div>
        );
    }

    return (
        <div className={styles.pageContainer}>
            {/* ===== HEADER ===== */}
            <TalentNavbar
                firstName={hygienist?.firstName}
                lastName={hygienist?.lastName}
                photoUrl={hygienist?.photoUrl || undefined}
                isHygOrAsst={hygienist?.type === 0 || hygienist?.type === 1}
                instabookActivated={hygienist?.InstabookActivated === 1}
                isTestAccount={(hygienist as any)?.isTest === 1}
                showAddShiftButton={true}
                showJobBoardButton={true}
                hireJobsCount={hireJobsCount}
                onJobBoardClick={handleJobBoardClick}
                hideAddShiftOnMobile
                pageTitle="Booked/Confirmed Shift"
            />

            {/* ===== MAIN CONTENT ===== */}
            {!isJobEmpty && (
                <main className={styles.mainContent}>
                    {/* T2-6759: standard Talent back pill under navbar (T2-6194), not icon-only in toolbar */}
                    <TalentBackButton onBack={handleBackNavigation} />
                    <TalentPageTitle title="Booked/Confirmed Shift" />
                    {/* ===== SUBHEADER ===== */}
                    <div className={styles.subheader}>
                        <div className={styles.subheaderActions}>
                            <div className={styles.statusGroup}>
                                <div className={styles.bookedBadge}>
                                    <div className={styles.bookedDot}></div>
                                    Booked
                                </div>
                                <button className={styles.locationButton} onClick={handleViewMap}>
                                    <RoomOutlinedIcon />
                                    Location
                                </button>
                                <button
                                    className={styles.heartButton}
                                    onClick={handleToggleFavourite}
                                    disabled={updatingFavourite}
                                >
                                    <img
                                        src={isFavouriteDentist ? heartFilled : heartUnfilled}
                                        alt={isFavouriteDentist ? 'Remove from favourites' : 'Add to favourites'}
                                        className={styles.heartIcon}
                                    />
                                </button>
                            </div>
                            <div className={styles.actionGroup}>
                                <button className={styles.cancelShiftButton} onClick={handleCancelJob}>
                                    Cancel shift
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Main Content Box - wraps everything from office name to bottom */}
                    <div className={styles.mainContentBox}>
                        <p className={styles.confirmationNote}>{confirmationNote}</p>
                        {/* Office Card */}
                        <div className={styles.officeCard}>
                            <div className={styles.officeNameRow}>
                                <div className={styles.officeNameSection}>
                                    <h1 className={styles.officeName}>{job.location?.practiceName}</h1>
                                    <div className={styles.officeLocationRow}>
                                        <p className={styles.officeLocation}>
                                            {job.location?.address || ''}, {job.location?.city}, {job.location?.province} {job.location?.postalCode}
                                        </p>
                                        <div className={styles.actionButtonsContainer}>
                                            <button className={styles.chatNowButton} onClick={handleChat}>
                                                Chat Now
                                            </button>
                                            <button 
                                                className={styles.viewDocumentButton} 
                                                onClick={handleDocumentView}
                                                disabled={!hasDocument}
                                            >
                                                View Office Document
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Divider above Shift Info */}
                        <hr className={styles.divider} />

                        {/* Shift Info Row - separate boxes */}
                        <div className={styles.shiftInfoRow}>
                            <div className={styles.shiftInfoItem}>
                                <div className={styles.shiftInfoLabel}>
                                    <div className={styles.shiftInfoIconCircle}>
                                        <CalendarTodayOutlinedIcon />
                                    </div>
                                    Shift Date
                                </div>
                                <div className={styles.shiftInfoValue}>{formattedShiftDate}</div>
                            </div>
                            <div className={styles.shiftInfoItem}>
                                <div className={styles.shiftInfoLabel}>
                                    <div className={styles.shiftInfoIconCircle}>
                                        <AccessTimeOutlinedIcon />
                                    </div>
                                    Shift Time
                                </div>
                                <div className={styles.shiftInfoValue}>{shiftTime}</div>
                            </div>
                            {job.shifts?.[0]?.firstPatientTime && (
                                <div
                                    className={styles.shiftInfoItem}
                                    id="confirmed-offer-first-patient-time"
                                    data-testid="confirmed-offer-first-patient-time"
                                >
                                    <div className={styles.shiftInfoLabel}>
                                        <div className={styles.shiftInfoIconCircle}>
                                            <AccessTimeOutlinedIcon />
                                        </div>
                                        First patient time
                                    </div>
                                    <div className={styles.shiftInfoValue}>
                                        {job.shifts[0].firstPatientTime}
                                    </div>
                                </div>
                            )}
                            <div className={styles.shiftInfoItem}>
                                <div className={styles.shiftInfoLabel}>
                                    <div className={styles.shiftInfoIconCircle}>
                                        <TimelapseOutlinedIcon />
                                    </div>
                                    Avg. Recall
                                </div>
                                <div className={styles.shiftInfoValue}>{avgRecall}</div>
                            </div>
                        </div>

                        {/* Posted On */}
                        <div className={styles.postedOn}>Posted On: {formattedPostedDate}</div>

                        {/* Reception/Admin duties + attire (T2-6509) */}
                        {job.type === 2 && job.adminTasks && (
                            <AdminTasksDisplay
                                adminTasks={job.adminTasks}
                                attire={job.adminTasks.attire}
                            />
                        )}

                        {/* Divider above Offer Rate */}
                        <hr className={styles.divider} />

                        {/* Offer Rate Card */}
                        {baseHourlyRate && (
                            <div className={styles.offerRateCard}>
                                <div className={styles.offerRateContent}>
                                    <div className={styles.offerRateTitle}>
                                        <AttachMoneyOutlinedIcon />
                                        Booked rate
                                    </div>
                                    <div className={styles.offerRateDescription}>{paymentDescription}</div>
                                </div>
                                <div className={styles.offerRateValue}>{hourlyRate}</div>
                            </div>
                        )}

                        {/* Details Grid */}
                        <div className={styles.detailsGrid}>
                            <div className={styles.detailItem}>
                                <div className={styles.detailIcon}><PersonOutlineOutlinedIcon /></div>
                                <div className={styles.detailContent}>
                                    <div className={styles.detailLabel}>Primary Contact</div>
                                    <div className={styles.detailValue}>{job.location?.primaryContact || '-'}</div>
                                </div>
                            </div>
                            <div className={styles.detailItem}>
                                <div className={styles.detailIcon}><WavesOutlinedIcon /></div>
                                <div className={styles.detailContent}>
                                    <div className={styles.detailLabel}>Ultrasonic</div>
                                    <div className={styles.detailValue}>{job.location?.ultrasonic || '-'}</div>
                                </div>
                            </div>
                            <div className={styles.detailItem}>
                                <div className={styles.detailIcon}><DesktopMacOutlinedIcon /></div>
                                <div className={styles.detailContent}>
                                    <div className={styles.detailLabel}>Software</div>
                                    <div className={styles.detailValue}>{job.location?.software || '-'}</div>
                                </div>
                            </div>
                            <div className={styles.detailItem}>
                                <div className={styles.detailIcon}><TimeToLeaveOutlinedIcon /></div>
                                <div className={styles.detailContent}>
                                    <div className={styles.detailLabel}>Parking</div>
                                    <div className={styles.detailValue}>{getTalentParkingLabel(job.location?.parking) || '-'}</div>
                                </div>
                            </div>
                            <div className={styles.detailItem}>
                                <div className={styles.detailIcon}><TimelapseOutlinedIcon /></div>
                                <div className={styles.detailContent}>
                                    <div className={styles.detailLabel}>Avg Recall</div>
                                    <div className={styles.detailValue}>{job.location?.avgApptTime || '-'}</div>
                                </div>
                            </div>
                            <div className={styles.detailItem}>
                                <div className={styles.detailIcon}><DiningOutlinedIcon /></div>
                                <div className={styles.detailContent}>
                                    <div className={styles.detailLabel}>Downtime</div>
                                    <div className={styles.detailValue}>
                                        {downtimeLabel != null ? downtimeLabel : '—'}
                                    </div>
                                </div>
                            </div>
                            <div className={styles.detailItem}>
                                <div className={styles.detailIcon}><BlurLinearOutlinedIcon /></div>
                                <div className={styles.detailContent}>
                                    <div className={styles.detailLabel}>Radiography</div>
                                    <div className={styles.detailValue}>{job.location?.radiography || '-'}</div>
                                </div>
                            </div>
                            <div className={styles.detailItem}>
                                <div className={styles.detailIcon}><LineAxisOutlinedIcon /></div>
                                <div className={styles.detailContent}>
                                    <div className={styles.detailLabel}>Charting</div>
                                    <div className={styles.detailValue}>{job.location?.charting || '-'}</div>
                                </div>
                            </div>
                            <div className={styles.detailItem}>
                                <div className={styles.detailIcon}><SpaOutlinedIcon /></div>
                                <div className={styles.detailContent}>
                                    <div className={styles.detailLabel}>Scent Policy</div>
                                    <div className={`${styles.detailValue} ${job.location?.scentFreePolicy !== 'scent-free' ? styles.warning : ''}`}>
                                        {job.location?.scentFreePolicy === 'scent-free' ? 'Scent-Free' : 'Not Scent-Free'}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Shift Notes Section - Only visible to booked talent */}
                        {job.shiftNotes && job.shiftNotes.trim() && (
                            <ShiftNotesPanel notes={job.shiftNotes} />
                        )}

                        {/* Endorsements Section */}
                        {hasEndorsements && (
                            <div className={styles.endorsementsSection}>
                                <div className={styles.endorsementsTitle}>
                                    Your fellow TempStars members have worked there and say:
                                </div>
                                <div className={styles.endorsementsList}>
                                    {job.location?.ppeEndorsed && (
                                        <div className={styles.endorsementItem}>
                                            <div className={`${styles.endorsementIcon} ${styles.ppe}`}>
                                                <SecurityOutlinedIcon />
                                            </div>
                                            <span className={styles.endorsementText}>PPE Endorsed</span>
                                        </div>
                                    )}
                                    {job.location?.friendlyEndorsed && (
                                        <div className={styles.endorsementItem}>
                                            <div className={`${styles.endorsementIcon} ${styles.friendly}`}>
                                                <SentimentSatisfiedAltOutlinedIcon />
                                            </div>
                                            <span className={styles.endorsementText}>Friendly team</span>
                                        </div>
                                    )}
                                    {job.location?.equipEndorsed && (
                                        <div className={styles.endorsementItem}>
                                            <div className={`${styles.endorsementIcon} ${styles.equipment}`}>
                                                <LaptopMacOutlinedIcon />
                                            </div>
                                            <span className={styles.endorsementText}>Good equipment</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* T2-6633: Mark completed below endorsements (or equivalent spot when none) */}
                        <div className={styles.markCompletedBelowEndorsements} data-testid="confirmed-offer-mark-completed">
                            <CompleteShiftModal job={job} history={history} trackData={trackData} />
                        </div>

                        {/* Safety Guidelines */}
                        <div className={styles.safetyGuidelines}>
                            <CheckCircleOutlineIcon />
                            <span className={styles.safetyText}>{safetyGuidelinesText}</span>
                        </div>

                        {/* Links Section */}
                        <div className={styles.linksAndCancelSection}>
                            <a
                                href={WORKPLACE_SAFETY_GUARANTEE_URL}
                                target="_system"
                                rel="noopener noreferrer"
                                className={`${styles.linkButton} external no-fastclick`}
                                id="talent-confirmed-safety-guarantee-link"
                                data-testid="talent-confirmed-safety-guarantee-link"
                                aria-label="Open workplace safety guarantee"
                                content-desc="talent-confirmed-safety-guarantee-link"
                                onClick={(event) => openExternalUrlFromLink(event.nativeEvent, WORKPLACE_SAFETY_GUARANTEE_URL)}
                            >
                                TempStars Workplace Safety Guarantee
                            </a>
                            <a
                                href={CANCELLATION_POLICY_URL}
                                target="_system"
                                rel="noopener noreferrer"
                                className={`${styles.linkButton} external no-fastclick`}
                                id="talent-confirmed-cancellation-policy-link"
                                data-testid="talent-confirmed-cancellation-policy-link"
                                aria-label="Open cancellation and membership review policy"
                                content-desc="talent-confirmed-cancellation-policy-link"
                                onClick={(event) => openExternalUrlFromLink(event.nativeEvent, CANCELLATION_POLICY_URL)}
                            >
                                TempStars Cancellation and Membership Review Policy
                            </a>
                        </div>

                        {/* Important Notes Section */}
                        <div className={styles.importantNotesSection}>
                            <div className={styles.importantNotesTitle}>Important Notes</div>
                            <div className={styles.importantNotesContent}>
                                <div className={styles.noteItem}>
                                    <strong>Lunch and Breaks:</strong> For most offices, lunch and breaks are unpaid,
                                    and offices do their best to provide a lunch break,
                                    but the nature of providing dental care to a full schedule can make this unpredictable.
                                    If you have concerns about a lunch break, be sure to ask the office owner or manager, or reach out
                                    to TempStars Support
                                </div>
                                <div className={styles.noteItem}>
                                    <strong>Start Time:</strong> This is the time the office indicated they need you at the office,
                                    changed into scrubs and ready for clinical work. End of shift can be a bit variable.
                                    If you need to end your shift absolutely no later than the scheduled end time,
                                    tell the office manager at the beginning of your shift, so they can accommodate you.
                                </div>
                                <div className={styles.noteItem}>
                                    <strong>Directly Arranged Shifts:</strong> If {job.location?.practiceName} arranges any shifts with you directly outside the app,
                                    record it in the "Book/Record Shift" button in the app.
                                    It's our policy to keep your membership in good standing and is the only way to benefit from
                                    TempStars guarantees and service.
                                    <a
                                        href={WORKPLACE_SAFETY_GUARANTEE_URL}
                                        target="_system"
                                        rel="noopener noreferrer"
                                        className={`${styles.noteLink} external no-fastclick`}
                                        id="talent-confirmed-direct-safety-guarantee-link"
                                        data-testid="talent-confirmed-direct-safety-guarantee-link"
                                        aria-label="Open workplace safety guarantee for directly arranged shifts"
                                        content-desc="talent-confirmed-direct-safety-guarantee-link"
                                        onClick={(event) => openExternalUrlFromLink(event.nativeEvent, WORKPLACE_SAFETY_GUARANTEE_URL)}
                                    >
                                        (TempStars Workplace Safety Guarantee)
                                    </a>
                                    <a
                                        href={DIRECT_PAYMENT_URL}
                                        target="_system"
                                        rel="noopener noreferrer"
                                        className={`${styles.noteLink} external no-fastclick`}
                                        id="talent-confirmed-payment-guarantee-link"
                                        data-testid="talent-confirmed-payment-guarantee-link"
                                        aria-label="Open payment guarantee"
                                        content-desc="talent-confirmed-payment-guarantee-link"
                                        onClick={(event) => openExternalUrlFromLink(event.nativeEvent, DIRECT_PAYMENT_URL)}
                                    >
                                        (TempStars Payment Guarantee)
                                    </a>
                                    <a
                                        href={SHORT_NOTICE_CANCELLATION_URL}
                                        target="_system"
                                        rel="noopener noreferrer"
                                        className={`${styles.noteLink} external no-fastclick`}
                                        id="talent-confirmed-short-notice-guarantee-link"
                                        data-testid="talent-confirmed-short-notice-guarantee-link"
                                        aria-label="Open short notice cancellation guarantee"
                                        content-desc="talent-confirmed-short-notice-guarantee-link"
                                        onClick={(event) => openExternalUrlFromLink(event.nativeEvent, SHORT_NOTICE_CANCELLATION_URL)}
                                    >
                                        (TempStars Short Notice Cancellation Guarantee)
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* End Main Content Box */}
                </main>
            )}

            <FixedLoader loading={isLoading} />
        </div>
    );
};

export default React.memo(ConfirmedOfferPage);
